const API_CACHE_CLEAR_TIMEOUT_MS = 1000;
const API_CACHE_SCOPE_KEY = 'h3ight-api-cache-scope';

function createApiCacheScope(): string {
  return `${Date.now()}-${crypto.randomUUID()}`;
}

function readApiCacheScope(): string {
  try {
    const storedScope = window.sessionStorage.getItem(API_CACHE_SCOPE_KEY);
    if (storedScope) return storedScope;

    const newScope = createApiCacheScope();
    window.sessionStorage.setItem(API_CACHE_SCOPE_KEY, newScope);
    return newScope;
  } catch {
    return createApiCacheScope();
  }
}

let apiCacheScope = readApiCacheScope();

export function getApiCacheScope(): string {
  return apiCacheScope;
}

function rotateApiCacheScope(): void {
  apiCacheScope = createApiCacheScope();
  try {
    window.sessionStorage.setItem(API_CACHE_SCOPE_KEY, apiCacheScope);
  } catch {
    // The in-memory scope still prevents cache reuse in this tab.
  }
}

export async function clearCachedApiResponses(): Promise<void> {
  rotateApiCacheScope();

  const cacheStorageClear = clearApiCacheStorage();
  const controller = navigator.serviceWorker?.controller;
  if (controller) {
    void requestWorkerApiCacheClear(controller)
      .then(async (cleared) => {
        if (!cleared) {
          await clearApiCacheStorage();
        }
      })
      .catch(() => {});
  }

  await Promise.allSettled([cacheStorageClear]);
}

function requestWorkerApiCacheClear(controller: ServiceWorker): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const channel = new MessageChannel();
    const timeout = window.setTimeout(() => {
      channel.port1.close();
      resolve(false);
    }, API_CACHE_CLEAR_TIMEOUT_MS);

    channel.port1.onmessage = (event) => {
      window.clearTimeout(timeout);
      channel.port1.close();
      resolve(event.data?.type === 'API_CACHE_CLEARED');
    };
    try {
      controller.postMessage({ type: 'CLEAR_API_CACHE' }, [channel.port2]);
    } catch {
      window.clearTimeout(timeout);
      channel.port1.close();
      resolve(false);
    }
  });
}

async function clearApiCacheStorage(): Promise<void> {
  if (!window.caches) return;

  const cacheNames = await window.caches.keys();
  await Promise.all(
    cacheNames.map(async (cacheName) => {
      const cache = await window.caches.open(cacheName);
      const requests = await cache.keys();
      await Promise.all(
        requests
          .filter((request) => new URL(request.url).pathname.startsWith('/api/'))
          .map((request) => cache.delete(request)),
      );
    }),
  );
}
